import { useState } from 'react'

import {
  Box,
  Button,
  List,
  Tag,
  Text,
  TogglerCheckbox,
} from '@factorialco/deprecated-design-system'
import {
  CompensationsPayrollConceptFragment,
  CompensationsPayrollConceptsDocument,
  CompensationsTogglePayrollConceptStatusDocument,
} from 'generated/resources/graphql'
import { CompensationsCreatePayrollConceptStatusDocument } from 'generated/resources/graphql'
import { isNil } from 'lodash'

import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import EditConceptModal from 'modules/compensations/pages/settings/PayrollConcepts/EditConceptModal'
import MergeConceptModal from 'modules/compensations/pages/settings/PayrollConcepts/MergeConceptModal'
import 'modules/compensations/pages/settings/PayrollConcepts/index.styles.css'

interface ConceptBlockProps {
  concept: CompensationsPayrollConceptFragment
  legacyPayroll?: boolean
  canMergeConcepts?: boolean
}

const ConceptBlock = ({
  concept,
  legacyPayroll = false,
  canMergeConcepts = false,
}: ConceptBlockProps) => {
  const [showEditModal, setShowEditModal] = useState<boolean>(false)
  const [showMergeModal, setShowMergeModal] = useState<boolean>(false)

  const [create] = useMutation(CompensationsCreatePayrollConceptStatusDocument, {
    extractorFn: (result) => result.compensationsMutations.createCompensationsPayrollConceptStatus,
  })

  const [toggle] = useMutation(CompensationsTogglePayrollConceptStatusDocument, {
    extractorFn: (result) => result.compensationsMutations.toggleCompensationsPayrollConceptStatus,
  })

  const onToggleCreate = async (newValue: boolean) => {
    const result = await create({
      variables: {
        companyId: concept.companyId,
        payrollConceptId: concept.id,
        enabled: newValue,
      },
      refetchQueries: [CompensationsPayrollConceptsDocument],
    })

    const handled = handleResult(result, () => undefined)
    return handled.success
  }

  const onToggleUpdate = async () => {
    if (isNil(concept.compensationsStatus?.id)) {
      throw new Error('No id')
    }

    const result = await toggle({
      variables: {
        id: concept.compensationsStatus.id,
      },
      refetchQueries: [CompensationsPayrollConceptsDocument],
    })

    const handled = handleResult(result, () => undefined)
    return handled.success
  }

  const onToggleConcept = async (newValue: boolean) => {
    const success = await (concept.compensationsStatus
      ? onToggleUpdate()
      : onToggleCreate(newValue))

    track(trackingEvents.compensations.togglePayrollConcept, {
      id: concept.id,
      enabled: newValue,
    })

    if (success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.save_error'),
      })
    }
  }

  const onMerge = () => {
    setShowMergeModal(true)
  }

  const canShowMergeButton =
    concept.compensationsStatus?.migrated && canMergeConcepts && !concept.default

  return (
    <>
      <List.Item key={concept.id} className={'PayrollConcepts_listSubitem'}>
        <List.Title>
          <Box paddingLeft="s24" flexDirection="row" gap="s8">
            <Text.Normal color="grey1000">{concept.label}</Text.Normal>
            {!concept.default && <Tag>{i18n.t('compensations.settings.concepts.custom')}</Tag>}
            {concept.compensationsStatus?.migrated && canMergeConcepts && (
              <Tag type="champagne">{i18n.t('compensations.settings.concepts.migrated')}</Tag>
            )}
          </Box>
        </List.Title>
        <List.ActionItem>
          <Box flexDirection="row" gap="s8">
            {canShowMergeButton && (
              <Button
                type="secondary"
                iconName="link"
                onClick={onMerge}
                aria-label={i18n.t('compensations.settings.concepts.merge')}
              >
                {i18n.t('compensations.settings.concepts.merge')}
              </Button>
            )}
            {!legacyPayroll && (
              <TogglerCheckbox
                onChange={onToggleConcept}
                value={concept.compensationsStatus?.enabled ?? true}
                aria-label={i18n.t('finance.settings.accounting.custom_categories.actions.toggle')}
              />
            )}
            <Button
              type="secondary"
              iconName="arrow-right"
              onClick={() => setShowEditModal(true)}
            ></Button>
          </Box>
        </List.ActionItem>
      </List.Item>
      {showEditModal && (
        <EditConceptModal
          readonly={concept.default}
          concept={concept}
          onSuccess={() => setShowEditModal(false)}
          onClose={() => setShowEditModal(false)}
          legacyPayroll={legacyPayroll}
        />
      )}
      {showMergeModal && (
        <MergeConceptModal
          concept={concept}
          onSuccess={() => setShowMergeModal(false)}
          onClose={() => setShowMergeModal(false)}
        />
      )}
    </>
  )
}

export default ConceptBlock
